import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

import { AuthenticationService } from 'app/authentication/service';
import { environment } from 'environments/environment';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  /**
   * Hard ceiling for any request. Without this a hung/slow request never settles,
   * so the global <block-ui> overlay raised by `blockUI.start()` is never released
   * and the whole UI (including the sidebar) becomes unclickable.
   */
  private readonly defaultTimeoutMs = environment.httpTimeout || 60000;
  /** File uploads (FormData) can legitimately take much longer than a normal API call. */
  private readonly uploadTimeoutMs = 5 * 60 * 1000;

  /**
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isUpload = typeof FormData !== 'undefined' && request.body instanceof FormData;
    const timeoutMs = isUpload ? this.uploadTimeoutMs : this.defaultTimeoutMs;

    return next.handle(request).pipe(
      timeout(timeoutMs),
      catchError((err) => {
        if (err instanceof TimeoutError) {
          // Settle the request so callers run their error path and stop the loading overlay.
          return throwError('Request timed out. Please check your connection and try again.');
        }
        if (err.status === 401) {
          // Auto logout only when token is invalid/expired.
          // 403 means permission denied and should not destroy the session.
          this._authenticationService.logout();
        }
        // throwError — guard against responses with no `error` body (e.g. network errors).
        const error = err?.error?.message || err?.statusText || 'Something went wrong. Please try again.';
        return throwError(error);
      })
    );
  }
}
